import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { normalizeRoleToken, userCanViewProducts, userIsNiStaff } from './roles';
import MainPage from './MainPage';
import DeploymentCheck from './pages/DeploymentCheck';
import DevicePage from './DevicePage';
import NorthstarPilot from './pages/quality/NorthstarPilot';
import NorthstarBrowser from './pages/quality/northstar-browser/NorthstarBrowser';
import NorthstarPackages from './pages/quality/northstar-browser/NorthstarPackages';
import TestToolList from './pages/quality/tools/TestToolList';
import TestToolDetail from './pages/quality/tools/TestToolDetail';
import TestRuntimeLab from './pages/quality/test-runtime/TestRuntimeLab';
import ProductsPage from './ProductsPage';
import ProductMetaPage from './ProductMetaPage';
import ProductSheetPage from './ProductSheetPage';
import RmaDashboard from './RmaDashboard';
import RmaArchivePage from './RmaArchivePage';
import AnalyticsPage from './AnalyticsPage';
import BuildOrderDetails from './BuildOrderDetails';
import BuildOrdersPage from './BuildOrdersPage';
import BuildOrderDevicePage from './BuildOrderDevicePage';
import ProductionStatusPage from './ProductionStatusPage';
import BuildOrderArchivePage from './BuildOrderArchivePage';
import CurrentStatsPage from './CurrentStatsPage';
import RmaReport from './RmaReport';
import PocTestDevicePage from './PocTestDevicePage';
import ShipmentsPage from './pages/service/ShipmentsPage';
import WaveConfiguratorPage from './WaveConfiguratorPage';
import OrgDevicesPage from './OrgDevicesPage';
import RefurbishedPage from './RefurbishedPage';
import HelpdeskPage from './HelpdeskPage';
import ComplaintsDashboard from './ComplaintsDashboard';
import SupplierComplaintPage from './SupplierComplaintPage';
import RecommissionPage from './RecommissionPage';
import { AdminOrgsPage, AdminOrgDetailPage } from './AdminOrgsPage';
import AdminUsersPage from './AdminUsersPage';
import BackfillPage from './BackfillPage';
import QualityLayout from './pages/quality/QualityLayout';
import QualityHubLanding from './pages/quality/QualityHubLanding';
import ChangeControlGuard from './pages/quality/changes/ChangeControlGuard';
import ChangeControlList from './pages/quality/changes/ChangeControlList';
import ChangeControlDetail from './pages/quality/changes/ChangeControlDetail';
import InvestigationGuard from './pages/quality/investigations/InvestigationGuard';
import InvestigationList from './pages/quality/investigations/InvestigationList';
import InvestigationDetail from './pages/quality/investigations/InvestigationDetail';
import QmsMigrationMaintenance from './pages/quality/QmsMigrationMaintenance';
import LaserEngravingPage from './pages/quality/laser/LaserEngravingPage';
import LaserTestDevicePage from './pages/quality/laser/LaserTestDevicePage';
import { laserEngravingAccessForUser } from './utils/laserEngravingAccess';
import StationOperate from './pages/quality/operate/StationOperate';
import QualityDocumentLibrary from './pages/quality/QualityDocumentLibrary';
import QualityNewDocument from './pages/quality/QualityNewDocument';
import QualityDocumentDetail from './pages/quality/QualityDocumentDetail';
import QualityDocumentEditor from './pages/quality/QualityDocumentEditor';
import QualityTemplatesPage from './pages/quality/QualityTemplatesPage';
import QualityObsoletePage from './pages/quality/QualityObsoletePage';
import QualityDocumentTree from './pages/quality/QualityDocumentTree';
import IcebergPage from './pages/quality/IcebergPage';
import ProcessFlowList from './pages/quality/process/ProcessFlowList';
import ProcessFlowNew from './pages/quality/process/ProcessFlowNew';
import ProcessFlowCanvas from './pages/quality/process/ProcessFlowCanvas';
import JigsLibrary from './pages/quality/jigs/JigsLibrary';
import JigNew from './pages/quality/jigs/JigNew';
import JigDetail from './pages/quality/jigs/JigDetail';
import SuppliersLibrary from './pages/quality/suppliers/SuppliersLibrary';
import SupplierNew from './pages/quality/suppliers/SupplierNew';
import SupplierDetail from './pages/quality/suppliers/SupplierDetail';
import EquipmentList from './pages/quality/equipment/EquipmentList';
import EquipmentNew from './pages/quality/equipment/EquipmentNew';
import EquipmentDetail from './pages/quality/equipment/EquipmentDetail';
import CalibrationDashboard from './pages/quality/equipment/CalibrationDashboard';
import TestLibraryList from './pages/quality/testlib/TestLibraryList';
import TestLibraryNew from './pages/quality/testlib/TestLibraryNew';
import TestLibraryDetail from './pages/quality/testlib/TestLibraryDetail';
import AssemblyInstructionList from './pages/quality/assembly/AssemblyInstructionList';
import AssemblyInstructionNew from './pages/quality/assembly/AssemblyInstructionNew';
import AssemblyInstructionDetail from './pages/quality/assembly/AssemblyInstructionDetail';
import StandardsLibrary from './pages/quality/standards/StandardsLibrary';
import BomList from './pages/quality/bom/BomList';
import BomUpload from './pages/quality/bom/BomUpload';
import BomDetail from './pages/quality/bom/BomDetail';
import NotesKanban from './pages/quality/notes/NotesKanban';
import PeGuard from './pages/quality/pe/PeGuard';
import PeWorkspace from './pages/quality/pe/PeWorkspace';
import PeActionDetail from './pages/quality/pe/PeActionDetail';
import QualityAnalyticsDashboard from './pages/quality/analytics/QualityAnalyticsDashboard';
import WorkOrderList from './pages/quality/execution/WorkOrderList';
import WorkOrderNew from './pages/quality/execution/WorkOrderNew';
import WorkOrderDetail from './pages/quality/execution/WorkOrderDetail';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import FarmLayout from './farm/FarmLayout';
import FarmMain from './farm/FarmMain';
import FarmDevice from './farm/FarmDevice';
import FarmGlobalMap from './farm/FarmGlobalMap';
import { normaliseTenant } from './farm/utils';
import LegacyRmaArchiveRedirect from './LegacyRmaArchiveRedirect';
import RmaQrRedirect from './RmaQrRedirect';
import TrainingLibrary from './pages/service/TrainingLibrary';
import SupportToolsHub from './pages/support/SupportToolsHub';
import SupportFlowPage from './pages/support/SupportFlowPage';
import SupportRolloutPage from './pages/support/SupportRolloutPage';
import SupportStartRmaPage from './pages/support/SupportStartRmaPage';
import ScmPage from './pages/scm/ScmPage';
import FeedbackButton from './components/FeedbackButton';
import SignInPrompt from './components/SignInPrompt';
import NoAccessNotice from './components/NoAccessNotice';
import WelcomeOnboarding from './components/WelcomeOnboarding';
import FeedbackDashboard from './pages/FeedbackDashboard';
import { init as initFeedbackCapture } from './utils/feedbackCapture';

initFeedbackCapture();

const normalizeRole = role => {
  if (!role) return '';
  if (Array.isArray(role)) {
    const firstString = role.find(value => typeof value === 'string');
    return firstString ? firstString.toLowerCase().trim() : '';
  }
  return String(role).toLowerCase().trim();
};

// Collect all roles from every metadata location Clerk may use
function collectRoles(user) {
  const sources = [
    user?.publicMetadata?.roles,
    user?.publicMetadata?.role,
    user?.privateMetadata?.roles,
    user?.privateMetadata?.role,
    user?.unsafeMetadata?.roles,
    user?.unsafeMetadata?.role,
  ];
  const set = new Set();
  sources.forEach(source => {
    if (Array.isArray(source)) {
      source.forEach(v => { const n = normalizeRoleToken(String(v)); if (n) set.add(n); });
    } else if (typeof source === 'string') {
      const n = normalizeRoleToken(source);
      if (n) set.add(n);
    }
  });
  return Array.from(set);
}

// On first sign-in, stamp the audience (internal vs external) on the user from
// their email domain, so it shows in the admin Users list. Never grants access
// — that only lives in publicMetadata.role, which the backend controls.
function PostLoginAudienceStamp() {
  const { isLoaded, isSignedIn, user } = useUser();
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return;
    if (user.unsafeMetadata?.audience) return;
    const email = user.primaryEmailAddress?.emailAddress
      || user.emailAddresses?.[0]?.emailAddress || '';
    const audience = (email.toLowerCase().split('@')[1] || '').includes('northinvent') ? 'internal' : 'external';
    user.update({ unsafeMetadata: { ...(user.unsafeMetadata || {}), audience } }).catch(() => {});
  }, [isLoaded, isSignedIn, user]);
  return null;
}

// Wraps an internal route so signed-out visitors (e.g. someone opening a shared
// link) get a clear sign-in prompt instead of a blank/broken page.
function RequireSignIn({ children, page }) {
  const { isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  return children;
}

// Service-only pages (Shipments, etc.) — admin or any service* role
function ServiceGuard({ children, page }) {
  const { user, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  const roles = collectRoles(user);
  const allowed = roles.includes('admin') || roles.some(r => r.startsWith('service'));
  if (!allowed) return <NoAccessNotice pageName={page} />;
  return children;
}

// Internal-staff pages with no supplier-facing content (/support/*) — admin,
// quality-manager, production-engineer, service*. Uses the same isStaff set as
// the backend, so suppliers never reach internal process documentation.
function NiStaffGuard({ children, page }) {
  const { user, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  if (!userIsNiStaff(user)) return <NoAccessNotice pageName={page} />;
  return children;
}

// Product catalogue (/products, /product/:n, /product-sheet) — NI staff + suppliers
// only. A supplier reaching it sees just their own products (backend-scoped); every
// other signed-in user is redirected away. Mirrors backend resolveProductAccess.
function ProductGuard({ children, page }) {
  const { user, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  if (!userCanViewProducts(user)) return <NoAccessNotice pageName={page} />;
  return children;
}

// Quality pages — service staff + quality roles + known supplier roles
const QUALITY_ALLOWED = new Set([
  'admin', 'quality-manager', 'production-engineer',
  'gpv', 'test-gpv', 'ubiqconn', 'acmepoint', 'hk_ringer',
]);
function QualityGuard({ children, page }) {
  const { user, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  const roles = collectRoles(user);
  const allowed = roles.some(r => QUALITY_ALLOWED.has(r) || r.startsWith('service'));
  if (!allowed) return <NoAccessNotice pageName={page} />;
  return children;
}

function LaserEngravingGuard({ children, page = 'laser engraving' }) {
  const { user, isLoaded, isSignedIn } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <SignInPrompt pageName={page} />;
  if (!laserEngravingAccessForUser(user).allowed) return <NoAccessNotice pageName={page} />;
  return children;
}

function FarmGuard({ role }) {
  const { tenant } = useParams();
  const normalizedTenant = normaliseTenant(tenant);
  const isAdmin = role === 'admin';
  if (!normalizedTenant.startsWith('farm-')) {
    return <Navigate to="/" replace />;
  }
  if (isAdmin || role === normalizedTenant) {
    return <FarmLayout role={role || 'guest'} />;
  }
  if (role.startsWith('farm-')) {
    return <Navigate to={`/farm/${role}`} replace />;
  }
  return <Navigate to="/" replace />;
}

function renderFarmRoutes(role) {
  return (
    <Route path="/farm/:tenant" element={<FarmGuard role={role} />}>
      <Route index element={<FarmMain />} />
      <Route path="map" element={<FarmGlobalMap />} />
      <Route path="uid/:uid" element={<FarmDevice />} />
    </Route>
  );
}

function App() {
  const { user } = useUser();
  const role = normalizeRole(user?.publicMetadata?.role);
  const isFarmRole = role.startsWith('farm-') && role !== 'admin';

  return (
    <>
      <Router>
        <FeedbackButton />
        <PostLoginAudienceStamp />
        <Routes>
          <Route path="/deployment-check" element={<DeploymentCheck />} />
          {isFarmRole ? (
            <>
              <Route path="/" element={<Navigate to={`/farm/${role}`} replace />} />
              {renderFarmRoutes(role)}
              <Route path="*" element={<Navigate to={`/farm/${role}`} replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={<MainPage />} />
              <Route path="/welcome" element={<WelcomeOnboarding />} />
              <Route path="/device/test/:id" element={<LaserEngravingGuard page="a test device"><LaserTestDevicePage /></LaserEngravingGuard>} />
              <Route path="/device/:id" element={<DevicePage />} />
              <Route path="/products" element={<ProductGuard page="the product catalogue"><ProductsPage /></ProductGuard>} />
              <Route path="/product-sheet" element={<ProductGuard page="the product sheet"><ProductSheetPage /></ProductGuard>} />
              <Route path="/product/:productNumber" element={<ProductGuard page="a product"><ProductMetaPage /></ProductGuard>} />
              <Route path="/rmas" element={<RequireSignIn page="the service board"><RmaDashboard /></RequireSignIn>} />
              <Route path="/rma-archive" element={<RequireSignIn page="the RMA archive"><RmaArchivePage /></RequireSignIn>} />
              <Route path="/rma/dashboard/archived" element={<LegacyRmaArchiveRedirect />} />
              <Route path="/rma/:rmaId" element={<RmaQrRedirect />} />
              <Route path="/analytics" element={<RequireSignIn page="Analytics"><AnalyticsPage /></RequireSignIn>} />
              <Route path="/build-orders" element={<RequireSignIn page="Build orders"><BuildOrdersPage /></RequireSignIn>} />
              <Route path="/build-order/:id" element={<RequireSignIn page="a build order"><BuildOrderDetails /></RequireSignIn>} />
              <Route path="/build-device/:id" element={<RequireSignIn page="a build device"><BuildOrderDevicePage /></RequireSignIn>} />
              <Route path="/production-status" element={<RequireSignIn page="Production status"><ProductionStatusPage /></RequireSignIn>} />
              <Route path="/build-order-archive" element={<RequireSignIn page="the build order archive"><BuildOrderArchivePage /></RequireSignIn>} />
              <Route path="/current-stats" element={<ServiceGuard page="Current stats"><CurrentStatsPage /></ServiceGuard>} />
              <Route path="/iceberg" element={<ServiceGuard page="Iceberg"><IcebergPage /></ServiceGuard>} />
              <Route path="/rma-report" element={<RmaReport />} />
              <Route path="/service/shipments" element={<ServiceGuard page="Shipments"><ShipmentsPage /></ServiceGuard>} />
              <Route path="/service/training" element={<RequireSignIn page="the training library"><TrainingLibrary /></RequireSignIn>} />
              <Route path="/support" element={<NiStaffGuard page="Support tools"><SupportToolsHub /></NiStaffGuard>} />
              <Route path="/support/flow" element={<NiStaffGuard page="the support flow"><SupportFlowPage /></NiStaffGuard>} />
              <Route path="/support/rollout" element={<NiStaffGuard page="the rollout plan"><SupportRolloutPage /></NiStaffGuard>} />
              <Route path="/support/start-rma" element={<NiStaffGuard page="Start an RMA"><SupportStartRmaPage /></NiStaffGuard>} />
              <Route path="/scm" element={<ServiceGuard page="SCM"><ScmPage /></ServiceGuard>} />
              <Route path="/wave-configurator" element={<WaveConfiguratorPage />} />
              <Route path="/org/devices" element={<RequireSignIn page="your organisation’s devices"><OrgDevicesPage /></RequireSignIn>} />
              <Route path="/service/refurbished" element={<RequireSignIn page="Refurbished units"><RefurbishedPage /></RequireSignIn>} />
              <Route path="/helpdesk" element={<HelpdeskPage />} />
              <Route path="/complaints" element={<RequireSignIn page="the NCR board"><ComplaintsDashboard /></RequireSignIn>} />
              <Route path="/supplier-complaint/:token" element={<SupplierComplaintPage />} />
              <Route path="/recommission" element={<RequireSignIn page="Recommissioning"><RecommissionPage /></RequireSignIn>} />
              {/* Station Operate Mode — fullscreen shop-floor kiosk (no quality shell) */}
              <Route path="/operate" element={<QualityGuard page="the station"><StationOperate /></QualityGuard>} />
              <Route path="/operate/:flowId" element={<QualityGuard page="the station"><StationOperate /></QualityGuard>} />
              <Route path="/operate/:flowId/:stationId" element={<QualityGuard page="the station"><StationOperate /></QualityGuard>} />
              <Route path="/quality" element={<QualityGuard page="the quality area"><QualityLayout /></QualityGuard>}>
                {/* Hub landing */}
                <Route index element={<QualityHubLanding />} />
                <Route path="northstar" element={<NorthstarPilot />} />
                <Route path="northstar/browser" element={<NorthstarBrowser />} />
                <Route path="northstar/packages" element={<NiStaffGuard page="Northstar configuration preparation"><NorthstarPackages /></NiStaffGuard>} />
                <Route path="test-tools" element={<TestToolList />} />
                <Route path="test-tools/new" element={<TestToolDetail creating />} />
                <Route path="test-tools/:documentNumber" element={<TestToolDetail />} />
                <Route path="test-runtime" element={<TestRuntimeLab />} />
                <Route path="change-control" element={<ChangeControlGuard><ChangeControlList /></ChangeControlGuard>} />
                <Route path="migration-maintenance" element={<ChangeControlGuard><QmsMigrationMaintenance /></ChangeControlGuard>} />
                <Route path="change-control/new" element={<ChangeControlGuard><ChangeControlDetail /></ChangeControlGuard>} />
                <Route path="change-control/:id" element={<ChangeControlGuard><ChangeControlDetail /></ChangeControlGuard>} />
                <Route path="sample-investigations" element={<InvestigationGuard><InvestigationList /></InvestigationGuard>} />
                <Route path="sample-investigations/new" element={<InvestigationGuard><InvestigationDetail /></InvestigationGuard>} />
                <Route path="sample-investigations/:id" element={<InvestigationGuard><InvestigationDetail /></InvestigationGuard>} />
                <Route path="laser-engraving" element={<LaserEngravingGuard><LaserEngravingPage /></LaserEngravingGuard>} />

                {/* Quality Documents */}
                <Route path="documents" element={<QualityDocumentLibrary />} />
                <Route path="documents/tree" element={<QualityDocumentTree />} />
                <Route path="documents/new" element={<QualityNewDocument />} />
                <Route path="documents/:id" element={<QualityDocumentDetail />} />
                <Route path="documents/:id/revisions/:revisionId" element={<QualityDocumentDetail />} />
                <Route path="documents/:id/edit-draft" element={<QualityDocumentEditor />} />
                <Route path="templates" element={<QualityTemplatesPage />} />
                <Route path="obsolete" element={<QualityObsoletePage />} />

                {/* Process Engineering */}
                <Route path="process" element={<ProcessFlowList />} />
                <Route path="process/new" element={<ProcessFlowNew />} />
                <Route path="process/:id" element={<ProcessFlowCanvas />} />

                {/* Supplier Matrix (issue #680) */}
                <Route path="suppliers" element={<SuppliersLibrary />} />
                <Route path="suppliers/new" element={<SupplierNew />} />
                <Route path="suppliers/:id" element={<SupplierDetail />} />

                {/* Jig Library */}
                <Route path="jigs" element={<JigsLibrary />} />
                <Route path="jigs/new" element={<JigNew />} />
                <Route path="jigs/:id" element={<JigDetail />} />

                {/* Equipment Registry */}
                <Route path="equipment" element={<EquipmentList />} />
                <Route path="equipment/new" element={<EquipmentNew />} />
                <Route path="equipment/:id" element={<EquipmentDetail />} />
                <Route path="calibration" element={<CalibrationDashboard />} />

                {/* Test Library */}
                <Route path="test-library" element={<TestLibraryList />} />
                <Route path="test-library/new" element={<TestLibraryNew />} />
                <Route path="test-library/:id" element={<TestLibraryDetail />} />

                {/* Assembly Instructions */}
                <Route path="assembly-instructions" element={<AssemblyInstructionList />} />
                <Route path="assembly-instructions/new" element={<AssemblyInstructionNew />} />
                <Route path="assembly-instructions/:id" element={<AssemblyInstructionDetail />} />

                {/* Bills of Materials */}
                <Route path="boms" element={<BomList />} />
                <Route path="boms/new" element={<BomUpload />} />
                <Route path="boms/:id" element={<BomDetail />} />

                {/* Compliance Standards catalogue */}
                <Route path="standards" element={<StandardsLibrary />} />

                {/* Notes & Actions Kanban */}
                <Route path="notes" element={<NotesKanban />} />
                <Route path="pe-task-follow-up" element={<PeGuard><PeWorkspace board="task-follow-up" /></PeGuard>} />
                <Route path="pe-production-improvement" element={<PeGuard><PeWorkspace board="production-improvement" /></PeGuard>} />
                <Route path="pe-actions/new" element={<PeGuard><PeActionDetail /></PeGuard>} />
                <Route path="pe-actions/:id" element={<PeGuard><PeActionDetail /></PeGuard>} />

                {/* QMS Analytics */}
                <Route path="analytics" element={<QualityAnalyticsDashboard />} />

                {/* Quality Execution */}
                <Route path="execution" element={<WorkOrderList />} />
                <Route path="execution/new" element={<WorkOrderNew />} />
                <Route path="execution/:id" element={<WorkOrderDetail />} />
              </Route>
              <Route path="/feedback" element={<ServiceGuard page="the feedback board"><FeedbackDashboard /></ServiceGuard>} />
              <Route path="/admin/users" element={<RequireSignIn page="user administration"><AdminUsersPage /></RequireSignIn>} />
              <Route path="/admin/organizations" element={<RequireSignIn page="organisation administration"><AdminOrgsPage /></RequireSignIn>} />
              <Route path="/admin/organizations/:orgId" element={<RequireSignIn page="an organisation"><AdminOrgDetailPage /></RequireSignIn>} />
              <Route path="/admin/backfill" element={<RequireSignIn page="the backfill tools"><BackfillPage /></RequireSignIn>} />
              {renderFarmRoutes(role)}
            </>
          )}
          <Route path="/" element={<MainPage />} />
          <Route path="/device/test/:id" element={<LaserEngravingGuard page="a test device"><LaserTestDevicePage /></LaserEngravingGuard>} />
          <Route path="/device/:id" element={<DevicePage />} />
          <Route path="/products" element={<ProductGuard page="the product catalogue"><ProductsPage /></ProductGuard>} />
          <Route path="/product/:productNumber" element={<ProductGuard page="a product"><ProductMetaPage /></ProductGuard>} />
          <Route path="/rmas" element={<RmaDashboard />} />
          <Route path="/rma-archive" element={<RmaArchivePage />} />
          <Route path="/rma/dashboard/archived" element={<LegacyRmaArchiveRedirect />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/build-orders" element={<BuildOrdersPage />} />
          <Route path="/build-order/:id" element={<BuildOrderDetails />} />
          <Route path="/build-device/:id" element={<BuildOrderDevicePage />} />
          <Route path="/production-status" element={<ProductionStatusPage />} />
          <Route path="/build-order-archive" element={<BuildOrderArchivePage />} />
          <Route path="/current-stats" element={<CurrentStatsPage />} />
          <Route path="/rma-report" element={<RmaReport />} />
          <Route path="/poc-test/devices/:id" element={<PocTestDevicePage />} />
          <Route path="/service/shipments" element={<ServiceGuard page="Shipments"><ShipmentsPage /></ServiceGuard>} />
          <Route path="/service/training" element={<TrainingLibrary />} />
          <Route path="/wave-configurator" element={<WaveConfiguratorPage />} />
          <Route path="/org/devices" element={<OrgDevicesPage />} />
          <Route path="/helpdesk" element={<HelpdeskPage />} />
          <Route path="/complaints" element={<ComplaintsDashboard />} />
          <Route path="/supplier-complaint/:token" element={<SupplierComplaintPage />} />
          <Route path="/admin/organizations" element={<AdminOrgsPage />} />
          <Route path="/admin/organizations/:orgId" element={<AdminOrgDetailPage />} />
          <Route path="/admin/backfill" element={<BackfillPage />} />
        </Routes>
      </Router>

      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App;
